import { useState } from 'react'
import {
  americanToDecimal,
  breakEvenDecimalOdds,
  decimalToAmerican,
  decimalToFractional,
  decimalToImplied,
  fractionalToDecimal,
  impliedToDecimal,
  marketOverround,
  removeVig,
  type VigMethod,
} from '../lib/math'
import { fmtPct } from '../lib/format'
import { Button, Card, NumberField, PageHead, SelectField } from '../components/ui'

export function OddsConverter() {
  const [american, setAmerican] = useState(-110)
  const [decimal, setDecimal] = useState(americanToDecimal(-110))
  const [fracNum, setFracNum] = useState(10)
  const [fracDen, setFracDen] = useState(11)
  const [probInput, setProbInput] = useState(0.524)

  const fromAmerican = () => setDecimal(americanToDecimal(american))
  const fromFractional = () => setDecimal(fractionalToDecimal({ numerator: fracNum, denominator: fracDen }))
  const fromProb = () => setDecimal(impliedToDecimal(probInput))

  const implied = decimalToImplied(decimal)
  const frac = decimalToFractional(decimal)

  return (
    <div className="stack">
      <PageHead title="Odds & Probability Converter">
        Convert between decimal, American and fractional odds, implied probability and break-even. Then strip the bookmaker's vig.
      </PageHead>

      <div className="grid cols-2">
        <Card title="Convert any format">
          <div className="grid cols-2">
            <NumberField label="Decimal" value={decimal} onChange={setDecimal} step={0.01} min={1.01} />
            <div>
              <NumberField label="American" value={american} onChange={setAmerican} step={5} />
              <Button size="sm" onClick={fromAmerican}>Use American →</Button>
            </div>
          </div>
          <div className="grid cols-2" style={{ marginTop: 8 }}>
            <div>
              <div className="row" style={{ gap: 8 }}>
                <NumberField label="Frac numerator" value={fracNum} onChange={setFracNum} />
                <NumberField label="Frac denominator" value={fracDen} onChange={setFracDen} />
              </div>
              <Button size="sm" onClick={fromFractional}>Use fractional →</Button>
            </div>
            <div>
              <NumberField label="Implied probability" value={probInput} onChange={setProbInput} step={0.01} min={0} max={1} />
              <Button size="sm" onClick={fromProb}>Use probability →</Button>
            </div>
          </div>
        </Card>

        <Card title="Result" subtitle="Derived from the decimal odds above">
          <div className="kv"><span className="k">Decimal</span><span className="v">{decimal.toFixed(3)}</span></div>
          <div className="kv"><span className="k">American</span><span className="v">{decimalToAmerican(decimal) > 0 ? '+' : ''}{decimalToAmerican(decimal)}</span></div>
          <div className="kv"><span className="k">Fractional</span><span className="v">{frac.numerator}/{frac.denominator}</span></div>
          <div className="kv"><span className="k">Implied probability</span><span className="v">{fmtPct(implied, 2)}</span></div>
          <div className="kv"><span className="k">Break-even win rate</span><span className="v">{fmtPct(implied, 2)}</span></div>
          <div className="kv"><span className="k">Fair odds at 50%</span><span className="v">{breakEvenDecimalOdds(0.5).toFixed(2)}</span></div>
        </Card>
      </div>

      <VigRemover />
    </div>
  )
}

function VigRemover() {
  const [legs, setLegs] = useState<number[]>([americanToDecimal(-110), americanToDecimal(-110)])
  const [method, setMethod] = useState<VigMethod>('proportional')

  const probs = legs.map((d) => decimalToImplied(d))
  const result = removeVig(probs, method)
  const overround = marketOverround(probs)

  const setLeg = (i: number, v: number) => setLegs((l) => l.map((x, idx) => (idx === i ? v : x)))

  return (
    <Card
      title="Vig / juice remover"
      subtitle="Enter every outcome's decimal odds. EdgeLab normalizes them into fair, no-vig probabilities."
      action={
        <div className="btn-row">
          <Button size="sm" onClick={() => setLegs((l) => [...l, 2])}>+ Outcome</Button>
          {legs.length > 2 && <Button size="sm" onClick={() => setLegs((l) => l.slice(0, -1))}>− Remove</Button>}
        </div>
      }
    >
      <div style={{ maxWidth: 280 }}>
        <SelectField
          label="Method"
          value={method}
          onChange={setMethod}
          options={[
            { value: 'proportional', label: 'Proportional (multiplicative)' },
            { value: 'additive', label: 'Additive (equal margin)' },
            { value: 'power', label: 'Power (favourite-longshot)' },
          ]}
        />
      </div>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr><th>Outcome</th><th>Decimal odds</th><th>Raw implied</th><th>Fair (no-vig)</th></tr>
          </thead>
          <tbody>
            {legs.map((d, i) => (
              <tr key={i}>
                <td>#{i + 1}</td>
                <td style={{ maxWidth: 140 }}>
                  <input className="input mono" type="number" step={0.01} value={d} onChange={(e) => setLeg(i, Number(e.target.value))} />
                </td>
                <td className="mono">{fmtPct(probs[i], 2)}</td>
                <td className="mono pos">{fmtPct(result.fair[i], 2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="row between" style={{ marginTop: 10 }}>
        <span className="muted">Overround: <span className="mono">{(overround * 100).toFixed(2)}%</span></span>
        <span className="muted">Bookmaker margin: <span className="mono warn">{result.marginPct.toFixed(2)}%</span></span>
      </div>
    </Card>
  )
}
